"use client";

import { AuthLink, Button, Input } from "components/ui/v2";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useMsal } from "@azure/msal-react";
import {
  NO_ORGANIZATION_REDIRECT,
  RECOVERY_PASSWORD_WITH_TOKEN,
  REFRESH_TOKEN_PERSIST,
  USER_LANG,
  USER_TOKEN_PERSIST
} from "@constants/global";
import Microsoft from "@public/svg/microsoft";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlCoins, gqlUser } from "gql";
import {
  IMicrosoftLoginResponse,
  ISocialNetworkLogInInput,
  ISocialNetworkLogInRes
} from "gql/User/mutations";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import setLanguage from "next-translate/setLanguage";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { isEmail } from "react-multi-email";
import { PUBLIC_ROUTES } from "routes/routes";
import { forceRefetchQueries } from "settings/apollo";
import { IUser } from "types/User.types";
import { getQueryOperator } from "utils/helpers";
import LinkedInButtonAurora from "./LinkedIn";

interface ILoginForm {
  callback?: (error?: string) => void;
  redirect?: string;
  invitationCode?: string;
}

const LoginFormAurora = (props: ILoginForm) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { callback, invitationCode, redirect } = props;
  const { instance } = useMsal();
  const { vendor } = useVendor();
  const [showPassword, setShowPassword] = useState(false);

  const [microsoftLoginState, setMicrosoftLoginState] = useState(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthCode = urlParams.has("code") || urlParams.has("state");
      const isMicrosoftAuthInProgress =
        sessionStorage.getItem("microsoftAuthInProgress") === "true";

      if (hasAuthCode || isMicrosoftAuthInProgress) {
        return {
          isProcessing: true,
          isRedirecting: false,
          error: null as string | null,
          timeoutId: null as NodeJS.Timeout | null
        };
      }
    }

    return {
      isProcessing: false,
      isRedirecting: false,
      error: null as string | null,
      timeoutId: null as NodeJS.Timeout | null
    };
  });

  const { setRefreshToken, setAccessToken, setUser, setBasketProducts } =
    useUserStoreContext((store) => ({
      setRefreshToken: store.setRefreshToken,
      setAccessToken: store.setAccessToken,
      setUser: store.setUser,
      setBasketProducts: store.setBasketProducts
    }));

  const [getUserBasket] = useLazyQuery(gqlCoins.queries.USER_BASKET, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    onCompleted: ({ basket }) => {
      const { basketMemory } = basket;
      setBasketProducts(
        basketMemory.map(({ product, quantity }) => ({
          product,
          quantity
        })) || []
      );
    }
  });

  const [loginWithMicrosoft, { loading: microsoftLoading }] = useMutation<
    { loginWithMicrosoft: IMicrosoftLoginResponse },
    { microsoftLogInInput: ISocialNetworkLogInInput }
  >(gqlUser.mutations.LOGIN_WITH_MICROSOFT, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_FULL_USER)],
    awaitRefetchQueries: true,
    onCompleted({ loginWithMicrosoft }) {
      sessionStorage.removeItem("microsoftAuthInProgress");
      setMicrosoftLoginState((prev) => ({
        ...prev,
        isProcessing: false,
        isRedirecting: false,
        error: null
      }));
      onCompletedLogin(loginWithMicrosoft);
      if (callback) {
        callback();
        return;
      }
      if (invitationCode) {
        router.push({
          pathname: PUBLIC_ROUTES.invitation.path,
          query: { t: invitationCode, redirect: redirect as string }
        });
      }
    },
    onError(error) {
      console.error("Microsoft login mutation failed:", error);
      sessionStorage.removeItem("microsoftAuthInProgress");
      setMicrosoftLoginState((prev) => ({
        ...prev,
        isProcessing: false,
        isRedirecting: false,
        error: t(
          "Error al iniciar sesión con Microsoft. Por favor, inténtalo de nuevo."
        )
      }));
    }
  });

  const handleMicrosoftLogin = async () => {
    try {
      sessionStorage.setItem("microsoftAuthInProgress", "true");
      setMicrosoftLoginState((prev) => ({
        ...prev,
        isRedirecting: true,
        error: null
      }));
      await instance.loginRedirect({
        prompt: "select_account",
        scopes: ["User.Read"]
      });
    } catch (error) {
      console.error("Microsoft login redirect failed:", error);
      sessionStorage.removeItem("microsoftAuthInProgress");
      setMicrosoftLoginState((prev) => ({
        ...prev,
        isRedirecting: false,
        error: t(
          "Error al conectar con Microsoft. Por favor, inténtalo de nuevo."
        )
      }));
    }
  };

  useEffect(() => {
    const handleMicrosoftRedirect = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthCode = urlParams.has("code") || urlParams.has("state");
        const isMicrosoftAuthInProgress =
          sessionStorage.getItem("microsoftAuthInProgress") === "true";

        if (hasAuthCode || isMicrosoftAuthInProgress) {
          setMicrosoftLoginState((prev) => ({
            ...prev,
            isProcessing: true,
            error: null
          }));
        }

        const timeoutId = setTimeout(() => {
          console.warn("Microsoft authentication timeout");
          setMicrosoftLoginState((prev) => ({
            ...prev,
            isProcessing: false,
            error: t(
              "La autenticación está tomando más tiempo del esperado. Por favor, inténtalo de nuevo."
            )
          }));
        }, 30000);

        setMicrosoftLoginState((prev) => ({ ...prev, timeoutId }));
        const tokenResponse = await instance.handleRedirectPromise();
        clearTimeout(timeoutId);

        if (tokenResponse) {
          await loginWithMicrosoft({
            variables: {
              microsoftLogInInput: {
                accessToken: tokenResponse?.accessToken
              }
            }
          });
        } else if (!hasAuthCode && !isMicrosoftAuthInProgress) {
          setMicrosoftLoginState((prev) => ({ ...prev, isProcessing: false }));
        }
      } catch (error) {
        console.error("Microsoft redirect handling failed:", error);
        setMicrosoftLoginState((prev) => ({
          ...prev,
          isProcessing: false,
          error: t(
            "Error durante la autenticación con Microsoft. Por favor, inténtalo de nuevo."
          )
        }));
      }
    };

    handleMicrosoftRedirect();

    return () => {
      if (microsoftLoginState.timeoutId)
        clearTimeout(microsoftLoginState.timeoutId);
    };
  }, []);

  const [getUser, { data: getUserData }] = useLazyQuery<
    { user: IUser },
    { accessToken: string }
  >(gqlUser.queries.GET_FULL_USER, {
    fetchPolicy: "network-only",
    onCompleted: ({ user }) => {
      if (user && user.lang) {
        setLanguage(user.lang);
        localStorage.setItem(USER_LANG, user.lang);
      } else {
        setLanguage("en");
        localStorage.setItem(USER_LANG, "en");
      }
      setUser(user);
      if (user?.selectedVendor?.enableCoins) getUserBasket();
    }
  });

  const [login, { loading: loginLoading, error: loginError }] = useMutation(
    gqlUser.queries.LOGIN,
    {
      onCompleted({ login }) {
        onCompletedLogin(login);
      },
      onError({ message }) {
        if (callback) callback(message);
      }
    }
  );

  const onCompletedLogin = (login: ISocialNetworkLogInRes) => {
    const { accessToken, refreshToken } = login;
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    localStorage.setItem(USER_TOKEN_PERSIST, accessToken);
    localStorage.setItem(REFRESH_TOKEN_PERSIST, refreshToken);
    getUser({ variables: { accessToken } });
  };

  const {
    handleSubmit: handleLoginSubmit,
    control: loginControl,
    formState: { errors }
  } = useForm<{ email: string; password: string }>();

  const handleLogin = async ({ email, password }) => {
    try {
      const { data } = await login({
        variables: { loginInput: { email, password } }
      });
      if (data && data.login) onCompletedLogin(data.login);
    } catch (e) {
      console.error("Login error:", e);
    }
  };

  const processUserData = async (userData) => {
    const {
      user: { vendorList }
    } = userData;
    if (callback) {
      callback();
      return;
    }
    if (invitationCode) {
      router.push({
        pathname: PUBLIC_ROUTES.invitation.path,
        query: { t: invitationCode }
      });
      return;
    }
    await forceRefetchQueries([gqlUser.queries.GET_FULL_USER]);
    if (!vendorList?.length) router.push(NO_ORGANIZATION_REDIRECT);
  };

  useEffect(() => {
    if (getUserData) processUserData(getUserData);
  }, [getUserData]);

  const isMicrosoftBusy =
    microsoftLoading ||
    microsoftLoginState.isProcessing ||
    microsoftLoginState.isRedirecting;

  const microsoftLabel = microsoftLoginState.isRedirecting
    ? t("Redirigiendo a Microsoft")
    : isMicrosoftBusy
      ? t("Procesando autenticación")
      : t("Continuar con Microsoft");

  return (
    <>
      {microsoftLoginState.isProcessing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-md dark:bg-zinc-950/80">
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-100 bg-white px-10 py-8 shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
            <div className="relative flex h-12 w-12 items-center justify-center">
              <Loader2 className="absolute h-10 w-10 animate-spin text-primary" />
              <Microsoft />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {t("Autenticando con Microsoft")}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">
                {t("Completando tu inicio de sesión")}
              </p>
            </div>
          </div>
        </div>
      )}

      <form
        onSubmit={handleLoginSubmit(handleLogin)}
        className="space-y-4 sm:space-y-5"
      >
        {!vendor?.onlyAdLogin && (
          <div className="space-y-3 sm:space-y-3">
            <Controller
              name="email"
              control={loginControl}
              defaultValue=""
              rules={{ required: true, validate: (v) => isEmail(v) }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="email"
                  type="email"
                  label={t("Correo electrónico")}
                  size="compact"
                  autoComplete="email"
                  error={
                    errors.email
                      ? t("Debes ingresar un e-mail válido")
                      : undefined
                  }
                />
              )}
            />

            <Controller
              name="password"
              defaultValue=""
              control={loginControl}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="password"
                  type={showPassword ? "text" : "password"}
                  label={t("Contraseña")}
                  size="compact"
                  autoComplete="current-password"
                  error={
                    errors.password
                      ? t("Debes ingresar una contraseña")
                      : undefined
                  }
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      tabIndex={-1}
                      aria-label={
                        showPassword
                          ? "Ocultar contraseña"
                          : "Mostrar contraseña"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </button>
                  }
                />
              )}
            />
          </div>
        )}

        {loginError && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {loginError.message}
          </div>
        )}

        {microsoftLoginState.error && (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-950/30">
            <p className="text-sm text-red-600 dark:text-red-300">
              {microsoftLoginState.error}
            </p>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() =>
                setMicrosoftLoginState((p) => ({ ...p, error: null }))
              }
            >
              {t("Cerrar")}
            </Button>
          </div>
        )}

        {!vendor?.onlyAdLogin && (
          <div className="mx-auto !mt-6 w-4/5">
            <Button
              type="submit"
              width="full"
              size="md"
              loading={loginLoading}
              disabled={loginLoading || isMicrosoftBusy}
            >
              {t("Iniciar Sesión")}
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {vendor?.enableMicrosoftLogin && (
            <div className="mx-auto w-4/5">
              <Button
                type="button"
                variant="ghost"
                width="full"
                size="xs"
                loading={isMicrosoftBusy}
                disabled={isMicrosoftBusy || loginLoading}
                onClick={handleMicrosoftLogin}
                leftIcon={<Microsoft />}
              >
                {microsoftLabel}
              </Button>
            </div>
          )}

          {!vendor?.onlyAdLogin && vendor?.enableLoginWithLinkedin && (
            <LinkedInButtonAurora
              onCompleted={onCompletedLogin}
              parentLoading={loginLoading}
            />
          )}
        </div>

        {!vendor?.onlyAdLogin && (
          <div className="flex items-center justify-between">
            <AuthLink
              href={RECOVERY_PASSWORD_WITH_TOKEN}
              size="xs"
              tone="muted"
            >
              {t("¿Olvidaste tu contraseña?")}
            </AuthLink>
            <AuthLink
              href={{
                pathname: PUBLIC_ROUTES.register.path,
                query: {
                  redirect: redirect as string,
                  invitationCode: invitationCode as string
                }
              }}
              size="xs"
              tone="muted"
            >
              {t("Crear cuenta")}
            </AuthLink>
          </div>
        )}
      </form>
    </>
  );
};

export default LoginFormAurora;
